import * as React from "react";

/**
 * Distance (px) from the bottom that still counts as at the bottom, so a
 * sub-pixel scroll offset (zoomed pages) doesn't read as the reader leaving.
 */
const AT_BOTTOM_TOLERANCE_PX = 4;

/**
 * Result returned by useFollowBottom hook.
 */
export interface UseFollowBottomResult {
  /** Whether the container keeps itself scrolled to the bottom as content grows */
  isFollowing: boolean;

  /** Scroll to the bottom and follow new content from then on */
  followBottom: (behavior?: ScrollBehavior) => void;
}

/**
 * Hook that keeps a scroll container scrolled to the bottom while its content
 * grows, the way a chat follows a streaming reply.
 *
 * The container follows from mount, so it opens at the bottom and stays there
 * as late-rendering content (markdown, images, charts) lands. A scroll that
 * moves the viewport up and away from the bottom stops following; scrolling
 * back to the bottom, or calling `followBottom`, resumes it. Content growth
 * alone never stops following: only an upward scroll counts as the reader
 * leaving, so programmatic jumps up the thread (task panel, admin
 * scroll-to-message) also stop it.
 *
 * @param scrollContainerRef - Ref to the scrollable viewport
 * @param contentRef - Ref to the element whose size changes as content arrives
 * @param options.startFollowing - Set false when the view scrolls somewhere
 *   other than the bottom on mount, so the initial pin doesn't override it.
 *   Defaults to true.
 */
export function useFollowBottom(
  scrollContainerRef: React.RefObject<HTMLElement>,
  contentRef: React.RefObject<HTMLElement>,
  { startFollowing = true }: { startFollowing?: boolean } = {},
): UseFollowBottomResult {
  const [isFollowing, setIsFollowing] = React.useState(startFollowing);
  // The observers below outlive renders, so they read the latest value here.
  const isFollowingRef = React.useRef(startFollowing);

  const setFollowing = React.useCallback(function setFollowing(
    following: boolean,
  ) {
    isFollowingRef.current = following;
    setIsFollowing(following);
  }, []);

  React.useLayoutEffect(
    function followContentGrowth() {
      const container = scrollContainerRef.current;
      const content = contentRef.current;
      if (!container || !content) {
        return;
      }

      let lastScrollTop = container.scrollTop;
      function trackReaderScroll() {
        const distanceFromBottom =
          container!.scrollHeight -
          container!.scrollTop -
          container!.clientHeight;
        if (distanceFromBottom <= AT_BOTTOM_TOLERANCE_PX) {
          setFollowing(true);
        } else if (container!.scrollTop < lastScrollTop) {
          setFollowing(false);
        }
        lastScrollTop = container!.scrollTop;
      }

      // Fires once on observe (the initial pin) and on every size change of
      // the content (streaming, late layout) or viewport (input box resize).
      // The initial pin is instant so a thread opens at the bottom; later pins
      // glide, so a block that lands mid-stream doesn't jerk the view.
      let hasPinned = false;
      const observer = new ResizeObserver(function pinToBottom() {
        if (isFollowingRef.current) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: hasPinned ? "smooth" : "instant",
          });
        }
        hasPinned = true;
      });

      container.addEventListener("scroll", trackReaderScroll, {
        passive: true,
      });
      observer.observe(content);
      observer.observe(container);
      return () => {
        container.removeEventListener("scroll", trackReaderScroll);
        observer.disconnect();
      };
    },
    [scrollContainerRef, contentRef, setFollowing],
  );

  const followBottom = React.useCallback(
    function followBottom(behavior: ScrollBehavior = "instant") {
      setFollowing(true);
      const container = scrollContainerRef.current;
      container?.scrollTo({ top: container.scrollHeight, behavior });
    },
    [scrollContainerRef, setFollowing],
  );

  return { isFollowing, followBottom };
}
